/**
 * A diner's cart line as this app builds it (ORDR-165).
 *
 * It IS a framework `CartLine`, so the framework's cart, review and payment
 * stages (reached through `handoff`, see `OrderScreens.tsx`) take it as is, and
 * it carries what the framework line cannot: the portion, the COMM-179
 * selection tree and the kitchen note. The framework keeps the objects it is
 * handed (its reducer seeds, filters and never rebuilds them), so the same
 * object reaches `customerServices.priceCart`, which reads the extra fields
 * through `isOrderLine`.
 *
 * The framework `selections` map is DERIVED from the same picks, the portion
 * included, so the framework's `cartSignature` changes whenever the size or a
 * nested answer does and a quote is never mistaken for a different line's.
 *
 * THE KITCHEN NOTE IS NOT SENT. paio-commerce's `CartLine` has no note field
 * (measured on `f1c28c0`: `models/cart.py`, `LINE_WRITABLE_FIELDS`), so the
 * note stays on the client line and `kitchenNotes` exposes it for the checkout
 * lane (ORDR-166) to compose into the order-level `kitchen_note` (COMM-182).
 */

import type { CartLine } from "../framework/types.js";
import type { CatalogueGroup, CatalogueItem } from "./catalogue.js";
import { groupKey, lineEstimate, wireSelections, type Picks, type WireSelection } from "./selection.js";

/** The longest kitchen note the Item screen takes. */
export const NOTE_MAX_LENGTH = 280;

/** The framework selection key that carries the portion, so the signature sees the size. */
export const PORTION_SELECTION_KEY = "portion";

export interface OrderLine extends CartLine {
  readonly portionCode: string | null;
  readonly wireSelections: readonly WireSelection[];
  readonly note: string;
  /** Every chosen answer's name, nested ones included, for the Cart (ORDR-166). */
  readonly summary: string;
  /** DISPLAY estimate in minor units; the quote is the price. */
  readonly estimateMinor: number | null;
}

export function isOrderLine(line: CartLine): line is OrderLine {
  const candidate = line as Partial<OrderLine>;
  return "portionCode" in line && Array.isArray(candidate.wireSelections);
}

function visibleMap(groups: readonly CatalogueGroup[], picks: Picks, into: Record<string, string[]>, parentKey: string | null, via?: string): void {
  for (const group of groups) {
    const key = groupKey(parentKey, group.id, via);
    const chosen = (picks[key] ?? []).filter((id) => group.choices.some((choice) => choice.id === id));
    if (chosen.length > 0) {
      into[key] = [...chosen];
    }
    for (const choice of group.choices) {
      if (choice.child !== null && chosen.includes(choice.id)) {
        visibleMap([choice.child], picks, into, key, choice.id);
      }
    }
  }
}

/** The chosen answers' names, depth first, in the order the selection tree is sent. */
function answerNames(groups: readonly CatalogueGroup[], tree: readonly WireSelection[]): string[] {
  return tree.flatMap((entry) => {
    const choice = groups.find((group) => group.id === entry.group_id)?.choices.find((option) => option.id === entry.choice_id);
    if (choice === undefined) {
      return [];
    }
    return [choice.name, ...answerNames(choice.child === null ? [] : [choice.child], entry.children)];
  });
}

export interface BuildLineInput {
  readonly id: string;
  readonly item: CatalogueItem;
  readonly portionCode: string | null;
  readonly picks: Picks;
  readonly quantity: number;
  readonly note: string;
}

export function buildLine({ id, item, portionCode, picks, quantity, note }: BuildLineInput): OrderLine {
  const selections: Record<string, string[]> = {};
  if (portionCode !== null) {
    selections[PORTION_SELECTION_KEY] = [portionCode];
  }
  visibleMap(item.groups, picks, selections, null);
  const portion = item.portions.find((entry) => entry.code === portionCode);
  const tree = wireSelections(item.groups, picks);
  return {
    id,
    menuItemId: item.offerRef,
    name: portion === undefined ? item.name : `${item.name} · ${portion.name}`,
    quantity,
    selections,
    portionCode,
    wireSelections: tree,
    note: note.trim().slice(0, NOTE_MAX_LENGTH),
    summary: answerNames(item.groups, tree).join(", "),
    estimateMinor: lineEstimate(item, portionCode, picks, quantity),
  };
}

export interface KitchenNote {
  readonly lineId: string;
  readonly name: string;
  readonly note: string;
}

/** Every line's note, composed into the order's `kitchen_note` at Checkout. Blank notes are left out. */
export function kitchenNotes(lines: readonly CartLine[]): KitchenNote[] {
  return lines
    .filter(isOrderLine)
    .filter((line) => line.note !== "")
    .map((line) => ({ lineId: line.id, name: line.name, note: line.note }));
}
